/**
 * Line-art scenery for the atlas map: single-weight ink strokes at low
 * opacity, the way a field atlas draws its margins. Every prop is positioned
 * by its base point (bottom centre) and takes the land's ink colour.
 */

/** Where a prop stands, in canvas pixels. */
export interface AtlasPropProps {
  /** Horizontal centre of the prop. */
  x: number;
  /** Ground line the prop stands on. */
  y: number;
  /** Uniform scale. Defaults to 1. */
  scale?: number;
  /** Stroke colour, typically the land's accent. */
  ink: string;
}

const transform = ({x, y, scale = 1}: AtlasPropProps) =>
  `translate(${x} ${y}) scale(${scale})`;

const strokeProps = (ink: string) =>
  ({
    stroke: ink,
    strokeWidth: 1.4,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    fill: 'none',
  }) as const;

/** A conifer drawn as three nested chevrons on a stem. */
export function InkPine(props: AtlasPropProps) {
  return (
    <g transform={transform(props)} {...strokeProps(props.ink)}>
      <path d="M0 0 V-14" />
      <path d="M-16 -14 L0 -40 L16 -14 Z" />
      <path d="M-11 -30 L0 -50 L11 -30" />
      <path d="M-6 -44 L0 -58 L6 -44" />
    </g>
  );
}

/** A broadleaf tree: a scalloped crown over a forked trunk. */
export function InkTree(props: AtlasPropProps) {
  return (
    <g transform={transform(props)} {...strokeProps(props.ink)}>
      <path d="M0 0 V-22 M0 -22 L-9 -32 M0 -24 L8 -34" />
      <path d="M-20 -34 C-26 -46 -14 -58 -6 -52 C-2 -64 14 -64 16 -52 C26 -54 30 -40 20 -34 C24 -24 8 -20 2 -26 C-6 -18 -24 -24 -20 -34 Z" />
    </g>
  );
}

/** A low shrub: two overlapping scalloped arcs. */
export function InkShrub(props: AtlasPropProps) {
  return (
    <g transform={transform(props)} {...strokeProps(props.ink)}>
      <path d="M-22 0 C-24 -12 -14 -20 -6 -14 C-2 -24 12 -24 14 -14 C24 -16 26 -2 20 0 Z" />
      <path d="M-4 -14 C0 -10 2 -6 2 0" />
    </g>
  );
}

/** Reeds: a fan of tapering strokes. */
export function InkReeds(props: AtlasPropProps) {
  return (
    <g transform={transform(props)} {...strokeProps(props.ink)}>
      <path d="M-8 0 C-9 -10 -8 -18 -5 -26" />
      <path d="M-2 0 C-3 -12 0 -22 4 -32" />
      <path d="M4 0 C5 -9 8 -15 12 -20" />
      <path d="M0 0 C1 -8 -1 -14 -3 -18" />
    </g>
  );
}

/** A compass rose: crossed needles in a fine ring, north marked. */
export function InkCompass(props: AtlasPropProps) {
  return (
    <g transform={transform(props)} {...strokeProps(props.ink)}>
      <circle cx={0} cy={-28} r={22} />
      <circle cx={0} cy={-28} r={2.5} fill={props.ink} stroke="none" />
      <path d="M0 -50 L4 -28 L0 -6 L-4 -28 Z" />
      <path d="M-22 -28 L0 -24 L22 -28 L0 -32 Z" />
      <path d="M0 -50 L4 -28 L0 -28 Z" fill={props.ink} stroke="none" />
      <text
        x={0}
        y={-56}
        textAnchor="middle"
        fontSize={9}
        fontFamily="'Source Sans 3', system-ui, sans-serif"
        fontWeight={700}
        letterSpacing={1}
        fill={props.ink}
        stroke="none"
      >
        N
      </text>
    </g>
  );
}

/** A small cairn of three stacked stones. */
export function InkCairn(props: AtlasPropProps) {
  return (
    <g transform={transform(props)} {...strokeProps(props.ink)}>
      <path d="M-14 0 C-16 -8 -6 -12 0 -10 C6 -12 16 -8 14 0 Z" />
      <path d="M-9 -10 C-10 -18 -2 -21 2 -19 C8 -21 12 -16 9 -10 Z" />
      <path d="M-5 -19 C-5 -25 3 -27 5 -22 C6 -20 5 -19 5 -19 Z" />
    </g>
  );
}
